import { useState, FormEvent, useMemo, ChangeEvent } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { SelectOption } from "@idw-types/common";
import { DefaultModal } from "../../common/modals/default-modal/default-modal";
import { resources } from "@constants/hardcoded-strings";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ButtonTypes } from "@enums/button-types";
import { SelectFormGroup } from "../../common/select-form-group/select-form-group";
import { AvailablePropertyTypes } from "@enums/available-property-types";
import { ModalSizeTypes } from "@enums/modal-size-types";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { RpServiceConfigurationPropertyFormGroup } from "../../common/rp-service-configuration-property-form-group/rp-service-configuration-property-form-group";
import { sortStrings } from "@utils/string-util";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";

type AddRpServiceConfigurationPropertyModalProps = {
    onModalClose: () => void;
    rpServiceConfigurationProperties: Array<FormattedRpServiceConfigurationProperty>;
    addRpServiceConfigurationProperty: (rpServiceConfigurationProperty: FormattedRpServiceConfigurationProperty) => void;
    title?: string;
};

type AddRpServiceConfigurationPropertyModalState = {
    rpServiceConfigurationProperty: FormattedRpServiceConfigurationProperty;
    selectedRpServiceConfigurationPropertyOption: SelectOption;
    textValue: string;
    selectOptions: Array<SelectOption>;
    selectedSelectOption: SelectOption;
};

export const AddRpServiceConfigurationPropertyModal = (props: AddRpServiceConfigurationPropertyModalProps) => {
    const { onModalClose, rpServiceConfigurationProperties, addRpServiceConfigurationProperty, title =  resources.modals.addRpServiceConfigurationProperty.title } = props;

    const [labelWidth] = useState(LabelWidthTypes.SMALL);
    const [state, setState] = useState<AddRpServiceConfigurationPropertyModalState>({
        rpServiceConfigurationProperty: null,
        selectedRpServiceConfigurationPropertyOption: null,
        textValue: "",
        selectOptions: [],
        selectedSelectOption: null
    });

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        let value = "";
        if ([AvailablePropertyTypes.BOOLEAN, AvailablePropertyTypes.SELECT].includes(state.rpServiceConfigurationProperty.type)) {
            value = state.selectedSelectOption?.value || "";
        } else if (
            [
                AvailablePropertyTypes.INTEGER,
                AvailablePropertyTypes.TEXT,
                AvailablePropertyTypes.TEXTAREA,
                AvailablePropertyTypes.STRING,
                AvailablePropertyTypes.JSON
            ].includes(state.rpServiceConfigurationProperty.type)) {
            value = state.textValue || "";
        }
        addRpServiceConfigurationProperty({
            ...state.rpServiceConfigurationProperty,
            value: value,
            addedToService: true
        });
        onModalClose();
    };

    const handleAvailablePropertyChange = (newValue: SelectOption) => {
        const rpServiceConfigurationProperty = rpServiceConfigurationProperties.find(ap => ap.propertyTypeId === newValue.value);
        let textValue = "";
        let selectOptions: Array<SelectOption> = [];
        let selectedSelectOption = null;
        if (rpServiceConfigurationProperty.values && [AvailablePropertyTypes.SELECT].includes(rpServiceConfigurationProperty.type)) {
            try {
                const jsonValues: Array<SelectOption> = JSON.parse(rpServiceConfigurationProperty.values);
                selectOptions = jsonValues;
            } catch (error) {
                console.error(`There was an error parsing the values for available property ${rpServiceConfigurationProperty.propertyTypeId} named ${rpServiceConfigurationProperty.shortName}. Error: ${error}`);
            }
        } else if ([AvailablePropertyTypes.BOOLEAN].includes(rpServiceConfigurationProperty.type)) {
            selectOptions = BOOLEAN_RADIO_INPUT_OPTIONS;
            selectedSelectOption = selectOptions[0];
        } else if (rpServiceConfigurationProperty.values) {
            textValue = rpServiceConfigurationProperty.values;
        }
        setState({
            rpServiceConfigurationProperty: rpServiceConfigurationProperty,
            selectedRpServiceConfigurationPropertyOption: newValue,
            textValue: textValue,
            selectOptions: selectOptions,
            selectedSelectOption: selectedSelectOption
        });
    };

    const availablePropertyOptions = useMemo(() => {
        return rpServiceConfigurationProperties
            .map(ap => ({ label: ap.displayName, value: ap.propertyTypeId }))
            .sort((a, b) => sortStrings(a.label, b.label));
    }, [rpServiceConfigurationProperties]);

    const onChangeRadio = (newValue: string) => {
        const selectedRadioOption = state.selectOptions.find(so => so.value === newValue) || null;
        setState({
            ...state,
            selectedSelectOption: selectedRadioOption
        });
    };

    const onChangeText = (event: ChangeEvent<HTMLInputElement>) => {
        setState({
            ...state,
            textValue: event.target.value
        });
    };

    const onChangeSelect = (newValue: SelectOption) => {
        setState({
            ...state,
            selectedSelectOption: newValue
        });
    };

    return (
        <DefaultModal
            automationId="add-available-property-modal"
            ariaLabelledBy="add-available-property-title"
            onHide={onModalClose}
            backdrop={"static"}
            keyboard={false}
            isMaxWidth={false}
            size={ModalSizeTypes.MEDIUM}
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header id={"add-available-property-title"} closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <SelectFormGroup
                        controlId={"available-property-select"}
                        automationId={"available-property-select"}
                        ariaLabel={resources.modals.addRpServiceConfigurationProperty.inputs.property.label}
                        label={resources.modals.addRpServiceConfigurationProperty.inputs.property.label}
                        disabled={false}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={true}
                        labelWidth={labelWidth}
                        onChange={handleAvailablePropertyChange}
                        options={availablePropertyOptions}
                        selectedOption={state.selectedRpServiceConfigurationPropertyOption}
                    />
                    {state.rpServiceConfigurationProperty && (
                        <RpServiceConfigurationPropertyFormGroup
                            rpServiceConfigurationProperty={state.rpServiceConfigurationProperty}
                            controlId={`available-property-${state.rpServiceConfigurationProperty.propertyTypeId}`}
                            automationId={`available-property-${state.rpServiceConfigurationProperty.propertyTypeId}`}
                            disabled={false}
                            isRequired={true}
                            label={resources.modals.addRpServiceConfigurationProperty.inputs.value.label}
                            value={state.textValue}
                            selectValue={state.selectedSelectOption}
                            options={state.selectOptions}
                            onChangeText={onChangeText}
                            onChangeSelect={onChangeSelect}
                            onChangeRadio={onChangeRadio}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button id="add-rp-service-configuration-property-cancel-button" variant="secondary" onClick={onModalClose}>{resources.common.buttons.cancel.label}</Button>
                    <Button
                        id="add-rp-service-configuration-property-submit-button"
                        disabled={state.selectedRpServiceConfigurationPropertyOption === null}
                        variant="primary"
                        type={ButtonTypes.SUBMIT}
                    >
                        {resources.modals.addRpServiceConfigurationProperty.buttons.addProperty.label}
                    </Button>
                </Modal.Footer>
            </Form>
        </DefaultModal>
    );
};